import React, { useEffect, useMemo, useRef, useState } from "react";
import { MentionsInput, Mention } from 'react-mentions';
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";

import MsgSendButton from "./MsgSendButton";
import MsgReplyStatus from "./MsgReplyStatus";
import MsgTypingStatus from "./MsgTypingStatus";

import ChatService from '../ChatService';
import { ChatTypes } from '../chatReducer';

import styles from './index.module.scss'

const emojiExampleStyle = {
  flex: 1,

  control: {
    fontSize: 16,
    lineHeight: 1.2,
    minHeight: 63,
  },

  highlighter: {
    padding: 9,
    border: '1px solid transparent',
  },

  input: {
    fontSize: 16,
    lineHeight: 1.2,
    padding: 9,
    border: '1px solid silver',
    color: 'white'
  },

  suggestions: {
    list: {
      backgroundColor: 'white',
      border: '1px solid rgba(0,0,0,0.15)',
      fontSize: 16,
      zIndex: -1,
    },

    item: {
      padding: '5px 15px',
      borderBottom: '1px solid rgba(0,0,0,0.15)',
      color: 'black',

      '&focused': {
        backgroundColor: '#cee4e5',
      },
    },
  },
}

const defaultMentionStyle = {
  color: '#f6c548',
}

const typingStatusTimeTolerance = 1000;    // 1 seconds

function MessageInputPanel({ slowModeTimeRemaining }) {
  const dispatch = useDispatch();

  const { input: msgInput, searchInput, channelSelected, replyMsg, typingInfo } = useSelector(state => state.chat);
  const { user: auth_user } = useSelector(state => state.auth);
  const usernames = useSelector(state => state.misc.usernames || []);

  const typingStatusLastSentTS = useRef();

  const usernameList = useMemo(() => {
    let list = Array.isArray(usernames) ? usernames : [];
    return list.map(({username}) => ({
      id: username,
      display: username
    }))
  }, [usernames])

  const disableChat = slowModeTimeRemaining > 0 || (!!searchInput && !replyMsg);
  const inputPlaceholder = slowModeTimeRemaining > 0 ? "" : (searchInput && !replyMsg) ? "Clear search or reply" : "Mention people using '@'"

  const onChange = (text) => {
    dispatch({
      type: ChatTypes.SET_INPUT,
      value: text.target.value
    })
    const now = new Date().getTime();
    if (!typingStatusLastSentTS.current || now - typingStatusLastSentTS.current > typingStatusTimeTolerance) {
      ChatService.notifyTypingIndicator();
      typingStatusLastSentTS.current = now;
    }
  }

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      clearReplyStatus();
    }
    if (e.keyCode === 13) {
      if (e.ctrlKey || e.shiftKey || e.altKey) {
        dispatch({
          type: ChatTypes.SET_INPUT,
          value: msgInput + "\n"
        })
      } else {
        e.preventDefault();
        onSend();
      }
    }
  }

  const clearReplyStatus = () => {
    dispatch({
      type: ChatTypes.SET_REPLY_MSG,
      value: null
    })
  }

  const onSend = () => {
    let refinedInput = msgInput.trim();
    if (!refinedInput) {
      return;
    }
    refinedInput = refinedInput.replaceAll(/@[(](?<value>[^@)]+)[)]/g, function () {
      const group = arguments[arguments.length - 1];
      return `<span class="chat-msg-username">@${group.value}</span>`;
    })
    refinedInput = refinedInput.replaceAll(/[$][a-zA-z]+/g, function () {
      return `<span class="chat-msg-symbol">${arguments[0]}</span>`;
    })
    ChatService.sendMessage({
      channel: channelSelected,
      msg: refinedInput,
      replyMsg: replyMsg ? {
        id: replyMsg?.id,
        m: replyMsg?.content,
        ts: replyMsg?.created_at,
        u: {
          id: replyMsg?.user?.id,
          n: replyMsg?.user?.name,
        }
      } : null
    })
    dispatch({
      type: ChatTypes.SET_INPUT,
      value: ''
    })
    clearReplyStatus();
  }

  const typingStatus = useMemo(() => {
    if (auth_user && typingInfo) {
      let username, others;

      const filtered = (typingInfo.users || []).filter(item => item.id !== auth_user.id);
      username = filtered[0]?.name;
      others = filtered.length === (typingInfo.users || []).length ? typingInfo.tot - 1 : typingInfo.tot - 2;

      return username ? {
        username,
        others
      } : null;
    } else {
      return null;
    }
  }, [auth_user, typingInfo])

  return (
    <div className={`${styles['message-input']}`}>
      <div style={{width: '100%'}}>
        {
          typingStatus &&
          <MsgTypingStatus
            {
              ...typingStatus
            }
          />
        }
        {
          replyMsg &&
          <MsgReplyStatus
            replyUser={replyMsg?.user?.name}
            onClose={() => clearReplyStatus()}
          />
        }
        <div style={{width: '100%', position: 'relative'}}>
          <MentionsInput
            value={msgInput}
            onChange={onChange}
            onKeyDown={onKeyDown}
            className="mentions"
            classNames={styles}
            // style={emojiExampleStyle}
            placeholder={inputPlaceholder}
            disabled={disableChat}
            suggestionsPortalHost={window.document.body}
            allowSuggestionsAboveCursor={true}
          >
            <Mention
              trigger="@"
              displayTransform={(username) => `@${username}`}
              markup="@(__id__)"
              data={usernameList}
              // regex={/@(\S+)/}
              className={styles['mentions__mention__user']}
              // style={defaultMentionStyle}
              appendSpaceOnAdd
            />
            <Mention
              trigger="$"
              displayTransform={(symbol) => `$${symbol.toUpperCase()}`}
              markup="$__id__"
              data={[]}
              regex={/[$]([a-zA-z]+)/}
              className={styles['mentions__mention__stock']}
              // style={defaultMentionStyle}
              appendSpaceOnAdd
            />
          </MentionsInput>
          {
            slowModeTimeRemaining > 0 &&
            <div className={styles['slow-mode-status']}>
              <span>Slow mode: {slowModeTimeRemaining} sec</span>
            </div>
          }
        </div>
      </div>
      <MsgSendButton
        onClick={onSend}
        disabled={disableChat}
      />
    </div>
  )
}

MessageInputPanel.propTypes = {
  slowModeTimeRemaining: PropTypes.number
}

export default MessageInputPanel;
